import { FC, useContext, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { selfRegistration } from '@stax-core/truffle-tokens';
import SignInPage from './pages/Authentication/SignInPage';
import { SignUpPage } from './pages/Authentication/SignUpPage';
import { WelcomePage } from './pages/WelcomePage';
import { routes } from './routes/private';
import { history } from '../history';
import { BrandStore, ProgressStore } from '../context';
import { Tracker } from '../components/tracker/Tracker';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ApplicationLayout } from './components/layouts';

/**
 * The routes for the gogurt UI.
 * All of the routes (besides sign-in, sign-up, and welcome) are defined in the
 * './routes/private.tsx' file. The component in each private route is rendered
 * inside an '<ApplicationLayout ...>' as you can see in this code below.
 */
export const App: FC = () => {
  const location = useLocation();
  const { state: progressState } = useContext(ProgressStore);
  const { state: { brand } } = useContext(BrandStore);

  const [logo] = (brand && (brand.logoPngWideWhite || brand.logoPngWide)) || [];

  const savedPath = progressState?.currentStepPath;
  useEffect(() => {
    // the welcome page is the default, it will load if nothing is saved nor specified in the URL
    // not saving the welcome page fixes an issue with the saved pathname not restoring properly.
    // Also if the URL contains a Plaid OAuth token, then reload the last saved path.
    const pathWithHash = window.location.pathname + window.location.hash;
    const isOAuthRedirect = window.location.href.includes('?oauth_state_id');
    if ((pathWithHash === '/' || pathWithHash === '/#/' || isOAuthRedirect) && savedPath && savedPath !== '/' && savedPath !== '/welcome') {
      history.push(savedPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={{ ...selfRegistration, name: 'gogurt' } as any}>
      <Tracker>
        <Switch location={location}>
          <Route
            exact
            path="/sign-up"
            component={SignUpPage}
            key="sign-up-page"
          />
          <Route
            exact
            path="/sign-in"
            component={SignInPage}
            key="sign-in-page"
          />
          <Route
            exact
            path="/welcome"
            component={WelcomePage}
            key="welcome-page"
          />
          {routes.map((route, key) => (
            <Route
              key={key}
              path={route.path}
            >
              <ErrorBoundary>
                <ApplicationLayout logo={logo?.url}>
                  <route.component />
                </ApplicationLayout>
              </ErrorBoundary>
            </Route>
          ))}
          <Redirect from="/" to="/sign-in" />
          <Route path="*">
            <div>Not Found</div>
          </Route>
        </Switch>
      </Tracker>
    </ThemeProvider>
  );
};
